import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EnrollLandingPage = lazy(() => import('./pages/EnrollLandingPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const SsoLandingPage = lazy(() => import('./pages/SsoLandingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LearnerDashboardPage = lazy(() => import('./pages/LearnerDashboardPage'));
const ClassroomsPage = lazy(() => import('./pages/ClassroomsPage'));
const ClassroomDetailPage = lazy(() => import('./pages/ClassroomDetailPage'));
const DictionariesPage = lazy(() => import('./pages/DictionariesPage'));
const DictionaryDetailPage = lazy(() => import('./pages/DictionaryDetailPage'));
const ProbesPage = lazy(() => import('./pages/ProbesPage'));
const ProbeDetailPage = lazy(() => import('./pages/ProbeDetailPage'));
const MyClassesPage = lazy(() => import('./pages/MyClassesPage'));
const MyClassDetailPage = lazy(() => import('./pages/MyClassDetailPage'));
const TakeProbePage = lazy(() => import('./pages/TakeProbePage'));
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const AiChatPage = lazy(() => import('./pages/AiChatPage'));
const InstructorManagementPage = lazy(() => import('./pages/InstructorManagementPage'));
const TaxonomyPage = lazy(() => import('./pages/TaxonomyPage'));
const GoalConfigPage = lazy(() => import('./pages/GoalConfigPage'));

function RoleDashboard() {
  const { user } = useAuth();
  if (user?.role === 'Learner') return <LearnerDashboardPage />;
  return <DashboardPage />;
}

function App() {
  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Suspense
        fallback={
          <div className={cn('flex min-h-screen items-center justify-center')} role="status">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="sr-only">Loading page...</span>
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/enroll" element={<EnrollLandingPage />} />
          <Route path="/enroll/:code" element={<RegisterPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/sso" element={<SsoLandingPage />} />

          {/* Full-screen probe taking (no sidebar) */}
          <Route
            path="/take-probe"
            element={
              <ProtectedRoute allowedRoles={['Learner']}>
                <TakeProbePage />
              </ProtectedRoute>
            }
          />

          {/* Dashboard-layout routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<RoleDashboard />} />

            {/* Instructor / Admin routes */}
            <Route
              path="/classrooms"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <ClassroomsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/classrooms/:id"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <ClassroomDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dictionaries"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <DictionariesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dictionaries/:id"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <DictionaryDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/probes"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <ProbesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/probes/:probeId"
              element={
                <ProtectedRoute allowedRoles={['Instructor', 'OrgAdmin', 'UnitAdmin']}>
                  <ProbeDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['PlatformAdmin', 'OrgAdmin', 'UnitAdmin']}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/security" element={<SecurityPage />} />
            <Route
              path="/taxonomies"
              element={
                <ProtectedRoute allowedRoles={['PlatformAdmin', 'OrgAdmin', 'UnitAdmin']}>
                  <TaxonomyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/goal-config"
              element={
                <ProtectedRoute allowedRoles={['PlatformAdmin']}>
                  <GoalConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructors"
              element={
                <ProtectedRoute allowedRoles={['OrgAdmin', 'UnitAdmin']}>
                  <InstructorManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Learner routes */}
            <Route
              path="/my-classes"
              element={
                <ProtectedRoute allowedRoles={['Learner']}>
                  <MyClassesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-classes/:id"
              element={
                <ProtectedRoute allowedRoles={['Learner']}>
                  <MyClassDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/results"
              element={
                <ProtectedRoute allowedRoles={['Learner']}>
                  <ResultsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute allowedRoles={['Learner']}>
                  <AiChatPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
